import React from "react";
import { Mail, Phone, MessageCircle, Clock, CheckCircle, ExternalLink } from "lucide-react";
import SubmitComponent from "../components/SupportSubmitComponent";

const contacts = [
  {
    icon: Mail,
    title: "Email Support",
    detail: "support@aicareerhub.com",
    badge: "24-48h response",
  },
  {
    icon: Phone,
    title: "Phone Support",
    detail: "1-800-AI-CAREER",
    badge: "Mon-Fri 9AM-6PM EST",
  },
  {
    icon: MessageCircle,
    title: "Live Chat",
    detail: "Available in-app",
    badge: "Mon-Fri 9AM-6PM EST",
  },
];

const statuses = ["API Services", "AI Assessment", "Database"];

const resources = [
  "Getting Started Guide",
  "Account Settings",
  "Privacy & Security",
  "Billing & Subscriptions",
];

const SupportPage = () => (
  <div className="min-h-screen p-6">
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-pink-500 mb-2">Support Center</h1>
        <p className="text-gray-600 text-sm">Get help with your AI Career Hub experience</p>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left */}
        <div className="space-y-6">
          {/* Contact Options */}
          <Section title="Contact Options" icon={MessageCircle}>
            {contacts.map(({ icon: Icon, title, detail, badge }) => (
              <div key={title} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">{title}</p>
                    <p className="text-sm text-gray-600">{detail}</p>
                  </div>
                </div>
                <Badge>{badge}</Badge>
              </div>
            ))}
          </Section>

          {/* System Status */}
          <Section title="System Status" icon={Clock}>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="font-medium text-green-600">All Systems Operational</span>
            </div>
            <div className="space-y-3">
              {statuses.map((status) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-gray-700">{status}</span>
                  <Badge color="green">Online</Badge>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* Right */}
        <div className="space-y-6">
          {/* Submit Request */}
          <Section title="Submit a Support Request">
            <p className="text-gray-600 text-sm mb-6">Describe your issue and we'll get back to you soon</p>
            <SubmitComponent />
          </Section>

          {/* Quick Resources */}
          <Section title="Quick Resources">
            <p className="text-gray-600 text-sm mb-4">Common help topics</p>
            <div className="space-y-3">
              {resources.map((res) => (
                <button
                  key={res}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{res}</span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  </div>
);

// Reusable Section Wrapper
const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border">
    {title && (
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-5 h-5 text-gray-600" />}
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
    )}
    {children}
  </div>
);

// Badge Component
const Badge = ({ children, color = "gray" }) => (
  <span
    className={`px-3 py-1 rounded-full text-xs ${
      color === "green"
        ? "bg-green-100 text-green-700"
        : "bg-gray-200 text-gray-700"
    }`}
  >
    {children}
  </span>
);

export default SupportPage;
